/**
 * src/admin-ui/src/components/ResourceCard.tsx
 *
 * Reusable dashboard resource card for internal and external links.
 */

import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

interface ResourceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path?: string;
  url?: string;
  accent?: 'primary' | 'secondary';
  badge?: React.ReactNode;
}

const RouterCardLink = styled(Link)({
  display: 'block',
  height: '100%',
  color: 'inherit',
  textDecoration: 'none',
});

const ExternalCardLink = styled('a')({
  display: 'block',
  height: '100%',
  color: 'inherit',
  textDecoration: 'none',
});

const ResourceCardRoot = styled(Card)(({ theme }) => ({
  height: '100%',
  minHeight: 200,
  cursor: 'pointer',
  boxShadow: `inset -1px -1px 0 ${theme.palette.divider}`,
  transition: theme.transitions.create(['background-color', 'transform']),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:focus-within': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 160,
  },
}));

const ResourceCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: theme.spacing(2),
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
}));

const ResourceIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accent',
})<{ accent: 'primary' | 'secondary' }>(({ theme, accent }) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette[accent].main,
  '& .MuiSvgIcon-root': {
    fontSize: 48,
  },
}));

const ResourceDescription = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

const ResourceTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

/**
 * Render a dashboard card with one shared visual implementation.
 *
 * :param props: Resource content, destination and optional badge.
 * :returns: Linked resource card.
 */
export default function ResourceCard({
  title,
  description,
  icon,
  path,
  url,
  accent = 'primary',
  badge,
}: ResourceCardProps): React.ReactElement {
  const card = (
    <ResourceCardRoot square>
      <ResourceCardContent>
        <ResourceIcon accent={accent}>{icon}</ResourceIcon>
        <ResourceDescription variant="body2" color="text.secondary">
          {description}
        </ResourceDescription>
        <ResourceTitle variant="h6">
          {title}
          {badge}
        </ResourceTitle>
      </ResourceCardContent>
    </ResourceCardRoot>
  );

  if (url) {
    return (
      <ExternalCardLink href={url} target="_blank" rel="noopener noreferrer">
        {card}
      </ExternalCardLink>
    );
  }

  return <RouterCardLink to={path || '/dashboard'}>{card}</RouterCardLink>;
}
